// apps/embed/src/entries/bootConnectionHints.ts
//
// The connections a widget's first requests need, opened as its very first step (docs/embed-size-and-speed.md §6.1
// #1). The API and the photos are one server in Los Angeles with no CDN: from Europe each new connection spends
// 0.65-0.74 s on TCP and TLS, and until now it started only when the first request went out, after React, MUI and the
// storefront had evaluated (1.2 s on a phone). A `preconnect` added before them has the connection ready by then.
// Measured on a phone's first visit: the catalog 0.46 s sooner, the first photo 0.99 s sooner.
//
// An entry's FIRST import is its own side-effect module (shopBootHints.ts, chatBootHints.ts), which calls
// `addBootConnectionHints`: webpack evaluates an entry's imports in order, so the hints go out before the heavy
// modules evaluate. bootConnectionHints.test.ts pins that order and keeps these modules' imports light.
//
// It lives in the app, never in packages/storefront: a TourTask site renders the storefront on its server, and
// nothing of ours may write to that page's <head> (tools/ssr-harness).

import { apiV1BaseUrl, isLocalDev } from '@tourtask/storefront/api/baseUrls';
import { TOURTASK_MEDIA_ORIGIN } from '@tourtask/utils/shared/net/tourtaskOrigins';

/** A widget whose entry opens its connections first. */
export type BootHintWidget = 'shop' | 'chat';

export interface ConnectionHint {
	/** Scheme, host and port. */
	origin: string;
	/**
	 * Whether the requests it serves are anonymous CORS requests: the API calls, which the fetch client sends without
	 * credentials (`publicApiRequest`). The browser keeps those connections apart from credentialed ones, such as an
	 * `<img>` without `crossorigin`, so a hint in the other mode opens a connection nothing uses.
	 */
	anonymous: boolean;
}

/**
 * The hints for a widget: the API's for both, and the photos' for the shop (the chat shows none). None on the dev
 * machine, where the widget talks to the local API and its photos come from the host the local API names.
 */
export function bootConnectionHints(
	widget: BootHintWidget,
	where: { isLocalDev: boolean; apiV1BaseUrl: string } = { isLocalDev, apiV1BaseUrl },
): ConnectionHint[] {
	if (where.isLocalDev) return [];
	const api: ConnectionHint = { origin: new URL(where.apiV1BaseUrl).origin, anonymous: true };
	return widget === 'shop' ? [api, { origin: TOURTASK_MEDIA_ORIGIN, anonymous: false }] : [api];
}

/** A preconnect on the page that already opens the hint's connection: the same origin, in the same mode. */
function opensConnection(link: HTMLLinkElement, hint: ConnectionHint): boolean {
	let origin: string;
	try {
		origin = new URL(link.href).origin;
	} catch {
		return false; // no href, or not a URL: it opens nothing
	}
	return origin === hint.origin && (link.crossOrigin === 'anonymous') === hint.anonymous;
}

/**
 * Appends a `<link rel="preconnect">` to the head for each hint the page does not have yet. The partner's page, the
 * WordPress plugin and TourTask's own pages may already write them, as may the other widget's bundle on the same page.
 */
export function addConnectionHints(doc: Document, hints: readonly ConnectionHint[]): void {
	const present = Array.from(doc.querySelectorAll<HTMLLinkElement>('link[rel~="preconnect"]'));
	for (const hint of hints) {
		if (present.some((link) => opensConnection(link, hint))) continue;
		const link = doc.createElement('link');
		link.rel = 'preconnect';
		link.href = hint.origin;
		if (hint.anonymous) link.crossOrigin = 'anonymous';
		doc.head.appendChild(link);
		present.push(link);
	}
}

/**
 * The entry's first step. Nothing before `mountWidgetRoot` is inside the widget's error boundary, and a hint only
 * makes the widget faster, so a failure here must not stop the bundle: it is logged and the widget loads as it
 * always has, without the head start.
 */
export function addBootConnectionHints(widget: BootHintWidget): void {
	try {
		addConnectionHints(document, bootConnectionHints(widget));
	} catch (error) {
		console.warn('[embed] connection hints not added:', error);
	}
}
